import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateCustomDto,
  GetCustomResponse,
  GetCustomsResponse,
  GetCustomsV1Response,
  GetCustomV1BoResponse,
  GetCustomV1Response,
  PatchCustomDto,
  UpdatePartialCustomDto,
} from "../types/api/api-types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import { GetCustomsQueryDto } from "@/types/api/api-types";

const getCustomV1 = async (id: string) => {
  const response = await axiosApi.get<GetCustomV1Response>(`/custom/v1/${id}`);
  return response.data;
};

export const useGetCustomV1 = (id: string) => {
  return useQuery({
    queryKey: ["custom", "v1", id],
    queryFn: () => getCustomV1(id),
    enabled: !!id,
  });
};

const getCustomV1Bo = async (id: string) => {
  const response = await axiosApi.get<GetCustomV1BoResponse>(`/custom/v1/bo/${id}`);
  return response.data;
};

export const useGetCustomV1Bo = (id: string) => {
  return useQuery({
    queryKey: ["custom", "v1", "bo", id],
    queryFn: () => getCustomV1Bo(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 1, // 1 minute
    gcTime: 1000 * 60 * 1, // 1 minute
  });
};

const getCustoms = async (query?: GetCustomsQueryDto) => {
  const response = await axiosApi.get<GetCustomsResponse>(`/custom`, { params: query });
  return response.data;
};

export const useGetCustoms = (query?: GetCustomsQueryDto) => {
  return useQuery({
    queryKey: ["customs", query],
    queryFn: () => getCustoms(query),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

const getCustomsV1 = async (type: "quotation" | "customQuotation" = "quotation") => {
  const response = await axiosApi.get<GetCustomsV1Response>(`/custom/v1`, { params: { type } });
  return response.data;
};

export const useGetCustomsV1 = (type?: "quotation" | "customQuotation") => {
  return useQuery({
    queryKey: ["customs", "v1", type],
    queryFn: () => getCustomsV1(type),
  });
};

export const getCustom = async (id: string) => {
  const response = await axiosApi.get<GetCustomResponse>(`/custom/` + id);
  return response.data;
};

export const useGetCustom = (id: string) => {
  return useQuery({
    queryKey: ["custom", id],
    queryFn: () => getCustom(id),
  });
};

const createCustom = async (payload: CreateCustomDto) => {
  return axiosApi.post(`/custom`, payload);
};

export const useCreateCustom = () => {
  const { t } = useTranslation("boCustoms");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customs"] });
      toast.success(t("customsApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("customsApi.createError"));
    },
  });
};

const patchCustom = async (data: { id: string; payload: PatchCustomDto }) => {
  return axiosApi.patch(`/custom/${data.id}`, data.payload);
};

export const usePatchCustom = () => {
  const { t } = useTranslation("boCustoms");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchCustom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customs"] });
      toast.success(t("customsApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("customsApi.updateError"));
    },
  });
};

const archiveCustom = async (data: { id: string; payload: UpdatePartialCustomDto }) => {
  return axiosApi.patch(`/custom/archive/${data.id}`, data.payload);
};

export const useArchiveCustom = () => {
  const { t } = useTranslation("boCustoms");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archiveCustom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customs"] });
      toast.success(t("customsApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("customsApi.updateError"));
    },
  });
};
